/**
 * Canonical onboarding draft — maps 1:1 to mockProfiles listing shape.
 */

import { getDefaultCountry, getCountryByName } from "../../shared/data/locationTaxonomy";
import { normalizeProfileTypes } from "../../shared/data/profileTaxonomy";
import { formatFullPhone, getDialCodeForCountry, splitFullPhone } from "../../shared/utils/phoneUtils";
import { normalizeUsername } from "../../shared/utils/usernameValidation";
import { loadItem, storageKeys } from "../../shared/storage/index";

export const ONBOARDING_MODE = {
  CREATE: "create",
  EDIT: "edit",
};

export function resolveOnboardingMode(locationState) {
  return locationState?.mode === ONBOARDING_MODE.EDIT
    ? ONBOARDING_MODE.EDIT
    : ONBOARDING_MODE.CREATE;
}

export const ONBOARDING_STEP_COUNT = 5;

export const ONBOARDING_STEPS = [
  { key: "photos", path: "/onboarding/photos", titleKey: "onboarding.photos.title" },
  { key: "profile", path: "/onboarding/profile", titleKey: "onboarding.profile.title" },
  { key: "info", path: "/onboarding/info", titleKey: "onboarding.info.title" },
  { key: "services", path: "/onboarding/services", titleKey: "onboarding.services.title" },
  { key: "contact", path: "/onboarding/contact", titleKey: "onboarding.contact.title" },
];

export function getOnboardingStepIndex(stepKey) {
  return ONBOARDING_STEPS.findIndex((step) => step.key === stepKey);
}

export function getOnboardingStepByKey(stepKey) {
  return ONBOARDING_STEPS.find((step) => step.key === stepKey) ?? ONBOARDING_STEPS[0];
}

export function getNextOnboardingPath(stepKey) {
  const index = getOnboardingStepIndex(stepKey);
  const next = ONBOARDING_STEPS[index + 1];
  return next?.path ?? null;
}

export function getPrevOnboardingPath(stepKey) {
  const index = getOnboardingStepIndex(stepKey);
  const prev = ONBOARDING_STEPS[index - 1];
  return prev?.path ?? null;
}

export function normalizeLanguageEntries(languages) {
  if (!Array.isArray(languages)) return [];

  return languages
    .map((entry) => {
      if (typeof entry === "string") {
        const name = entry.trim();
        return name ? { name, stars: 4 } : null;
      }
      if (entry && typeof entry.name === "string") {
        const name = entry.name.trim();
        if (!name) return null;
        return {
          name,
          stars: typeof entry.stars === "number" ? entry.stars : 4,
        };
      }
      return null;
    })
    .filter(Boolean);
}

export function getOnboardingStepIndexFromPath(pathname) {
  const segment = pathname.replace(/\/$/, "").split("/").pop();
  return ONBOARDING_STEPS.findIndex((step) => step.path.endsWith(`/${segment}`));
}

const defaultCountry = getDefaultCountry();

export const DEFAULT_ONBOARDING_DRAFT = {
  photoSlots: Array(6).fill(null),
  photos: [],
  name: "",
  username: "",
  age: "",
  profileTypes: [],
  country: defaultCountry.name,
  countryCode: defaultCountry.code,
  city: "",
  desc: "",
  languages: [],
  services: [],
  serviceFor: [],
  contact: {
    whatsapp: "",
    whatsappDialCode: "+41",
    whatsappCountryCode: "CH",
    phone: "",
    phoneDialCode: "+41",
    phoneCountryCode: "CH",
    webcamEnabled: false,
  },
};

export function normalizeOnboardingDraft(draft = {}) {
  const contact = { ...DEFAULT_ONBOARDING_DRAFT.contact, ...(draft.contact || {}) };
  const photoSlots = Array.isArray(draft.photoSlots) && draft.photoSlots.length === 6
    ? draft.photoSlots.map((slot) => (
      typeof slot === "string" && slot.trim() && slot !== "__saved__" ? slot : null
    ))
    : Array(6).fill(null);

  const legacyPhotos = Array.isArray(draft.photos)
    ? draft.photos.filter((p) => typeof p === "string" && p.trim())
    : [];
  if (!photoSlots.some(Boolean) && legacyPhotos.length) {
    legacyPhotos.forEach((photo, index) => {
      if (index < 6) photoSlots[index] = photo;
    });
  }

  const photos = photoSlots.filter(Boolean);
  const uniquePhotos = [...new Set(photos)];
  const username = draft.username == null ? "" : normalizeUsername(draft.username);
  const legacyName = draft.name == null ? "" : String(draft.name).trim();

  return {
    ...DEFAULT_ONBOARDING_DRAFT,
    ...draft,
    photoSlots,
    photos: uniquePhotos,
    username,
    name: username || legacyName,
    age: draft.age == null ? "" : draft.age,
    desc: draft.desc == null ? "" : String(draft.desc),
    profileTypes: normalizeProfileTypes(Array.isArray(draft.profileTypes) ? draft.profileTypes : []),
    languages: normalizeLanguageEntries(draft.languages),
    services: Array.isArray(draft.services) ? draft.services : [],
    serviceFor: Array.isArray(draft.serviceFor) ? draft.serviceFor : [],
    contact,
  };
}

/** Merge separately stored onboarding photos back into the draft (after page reload). */
export function hydrateDraftWithPhotos(draft, storedPhotos) {
  const normalized = normalizeOnboardingDraft(draft);
  const hasInlinePhotos = normalized.photoSlots.some(Boolean);
  if (hasInlinePhotos) return normalized;

  const photos = Array.isArray(storedPhotos)
    ? storedPhotos.filter((item) => typeof item === "string" && item.trim())
    : [];
  if (!photos.length) return normalized;

  const photoSlots = Array(6).fill(null);
  photos.forEach((photo, index) => {
    if (index < 6) photoSlots[index] = photo;
  });

  return normalizeOnboardingDraft({
    ...normalized,
    photoSlots,
    photos,
  });
}

export function draftToProfile(draft, userId = "me") {
  const normalized = normalizeOnboardingDraft(draft);
  const photos = normalized.photos;
  const age = normalized.age === "" ? null : Number(normalized.age);
  const username = normalized.username;
  const name = username;
  const desc = String(normalized.desc ?? "").trim();
  const title = age ? `@${username}, ${age}` : `@${username}`;

  return {
    id: userId,
    name,
    username,
    age,
    title,
    displayName: username,
    desc,
    bio: desc,
    country: normalized.country,
    city: normalized.city,
    profileTypes: normalized.profileTypes,
    category: normalized.profileTypes[0] ?? "",
    services: normalized.services,
    serviceFor: normalized.serviceFor,
    languages: normalized.languages,
    photos,
    image: photos[0] ?? "",
    media: photos[0] ?? "",
    contact: {
      whatsapp: normalized.contact.whatsapp?.trim()
        ? formatFullPhone(
          normalized.contact.whatsappDialCode || normalized.contact.phoneDialCode,
          normalized.contact.whatsapp,
        )
        : "",
      phone: formatFullPhone(normalized.contact.phoneDialCode, normalized.contact.phone),
      webcamEnabled: Boolean(normalized.contact.webcamEnabled),
    },
    verified: false,
  };
}

export function profileToDraft(profile = {}) {
  const photos = Array.isArray(profile.photos)
    ? profile.photos.filter((item) => typeof item === "string" && item.trim())
    : [];
  const photoSlots = Array(6).fill(null);
  photos.forEach((photo, index) => {
    if (index < 6) photoSlots[index] = photo;
  });

  const countryName = profile.country || defaultCountry.name;
  const country = getCountryByName(countryName);
  const countryCode = country?.code || defaultCountry.code;
  const fallbackDial = getDialCodeForCountry(countryCode);

  const phoneParts = splitFullPhone(profile.contact?.phone, fallbackDial);
  const whatsappParts = splitFullPhone(profile.contact?.whatsapp, fallbackDial);

  return normalizeOnboardingDraft({
    photoSlots,
    photos,
    username: profile.username || normalizeUsername(profile.name || profile.displayName || ""),
    age: profile.age ?? "",
    profileTypes: profile.profileTypes?.length
      ? profile.profileTypes
      : profile.category
        ? [profile.category]
        : [],
    country: countryName,
    countryCode,
    city: profile.city || "",
    desc: profile.bio || profile.desc || "",
    languages: profile.languages || [],
    services: profile.services || [],
    serviceFor: profile.serviceFor || [],
    contact: {
      phone: phoneParts.national,
      phoneDialCode: phoneParts.dialCode,
      phoneCountryCode: countryCode,
      whatsapp: whatsappParts.national,
      whatsappDialCode: whatsappParts.dialCode,
      whatsappCountryCode: countryCode,
      webcamEnabled: Boolean(profile.contact?.webcamEnabled),
    },
  });
}

/** Strip photo blobs from draft before localStorage — photos live in profile storage. */
export function slimDraftForStorage(draft) {
  const normalized = normalizeOnboardingDraft(draft);
  return {
    ...normalized,
    photoSlots: normalized.photoSlots.map((slot) => (slot ? "__saved__" : null)),
    photos: [],
  };
}

export function mergeDraftSources(primaryDraft = {}, fallbackDraft = {}) {
  const primary = normalizeOnboardingDraft(primaryDraft);
  const fallback = normalizeOnboardingDraft(fallbackDraft);

  return normalizeOnboardingDraft({
    ...primary,
    username: primary.username || fallback.username,
    age: primary.age !== "" ? primary.age : fallback.age,
    profileTypes: primary.profileTypes.length ? primary.profileTypes : fallback.profileTypes,
    country: primary.country || fallback.country,
    countryCode: primary.countryCode || fallback.countryCode,
    city: primary.city || fallback.city,
    desc: primary.desc || fallback.desc,
    languages: primary.languages.length ? primary.languages : fallback.languages,
    services: primary.services.length ? primary.services : fallback.services,
    serviceFor: primary.serviceFor.length ? primary.serviceFor : fallback.serviceFor,
    photoSlots: primary.photos.length ? primary.photoSlots : fallback.photoSlots,
    contact: {
      ...fallback.contact,
      ...primary.contact,
      phone: primary.contact.phone || fallback.contact.phone,
      whatsapp: primary.contact.whatsapp || fallback.contact.whatsapp,
      phoneDialCode: primary.contact.phoneDialCode || fallback.contact.phoneDialCode,
      whatsappDialCode: primary.contact.whatsappDialCode || fallback.contact.whatsappDialCode,
      phoneCountryCode: primary.contact.phoneCountryCode || fallback.contact.phoneCountryCode,
      whatsappCountryCode: primary.contact.whatsappCountryCode || fallback.contact.whatsappCountryCode,
      webcamEnabled: primary.contact.webcamEnabled || fallback.contact.webcamEnabled,
    },
  });
}

export function readLocalOnboardingDraft() {
  const storedDraft = loadItem(storageKeys.onboardingDraft, DEFAULT_ONBOARDING_DRAFT);
  const storedPhotos = loadItem(storageKeys.profilePhotos, []);
  return hydrateDraftWithPhotos(storedDraft, storedPhotos);
}

export function toggleListItem(list, value) {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}
